/**
 * An SVG, and an Illustrator file, read for import.
 *
 * SVG is the best of the three formats this editor takes: an illustrator's
 * groups become layers, outlines stay outlines, and live text stays text. Where
 * a group uses something that cannot be drawn as outlines - a gradient, an
 * embedded photograph, a clipping path - that group alone is drawn as a picture
 * instead of the whole file being refused, which is what the library import
 * does and is right there and wrong here.
 *
 * An .ai file is a PDF with Illustrator's own data beside it. The layers live
 * in that private data and nothing outside Illustrator reads them, so an .ai
 * arrives as one piece. That is not a shortcut: there is nothing else in the
 * file to take apart.
 */

import { GraphicProblem, importSvg } from '../svg/import';
import { ImportProblem, PRINT_DPI, PT_TO_MM, mm, type ImportDoc, type ImportPiece } from './types';

/** Groups past this and a template stops being a template. */
const MOST_PIECES = 120;

/* ---------------------------------------------------------------
 * SVG
 * ------------------------------------------------------------ */

export async function readSvg( file: File, haveFont: ( family: string ) => string | null ): Promise<ImportDoc> {
  const text = await file.text();
  const root = new DOMParser().parseFromString( text, 'image/svg+xml' ).documentElement;

  if ( ! root || root.nodeName.toLowerCase() !== 'svg' ) {
    throw new ImportProblem( 'That file is not a drawing this can read.' );
  }

  const size = svgSize( root );

  if ( ! size ) {
    throw new ImportProblem( 'That drawing does not say what size it is. Give it a width, a height or a viewBox and export it again.' );
  }

  /*
   * The pieces: the top-level groups where the file has them, the whole
   * drawing where it does not. An illustrator's layer names come through as
   * id or data-name, which is why a file exported from Illustrator arrives
   * looking like the layer list it left as.
   */
  const groups = [ ...root.children ].filter( ( node ) => node.nodeName.toLowerCase() === 'g' );

  /*
   * Where each group sits, measured by the browser.
   *
   * Without this every group was given the whole page as its box, so a badge
   * an inch across arrived stretched over the card. getBBox is the only honest
   * answer: it accounts for transforms, strokes and nested groups, which
   * reading the numbers out of the file does not.
   */
  const boxes = measure( root, groups );
  const notes: string[] = [];
  const rastered: string[] = [];
  const pieces: ImportPiece[] = [];

  const asPiece = async ( node: Element, name: string, box: { x: number; y: number; w: number; h: number } ): Promise<ImportPiece | null> => {
    const alone = standalone( root, node, box );

    // Outlines first: they scale, they separate as vector on press, and every
    // colour in them is ink the customer can change.
    try {
      importSvg( alone, name );

      return {
        name,
        kind: 'outlines',
        box,
        hidden: node.getAttribute( 'display' ) === 'none',
        opacity: Number( node.getAttribute( 'opacity' ) ?? 1 ) || 1,
        svg: alone,
      };
    } catch ( error ) {
      if ( ! ( error instanceof GraphicProblem ) ) throw error;
    }

    // Something in it cannot be outlines - a gradient, a photograph, a
    // clipping path. That piece becomes a picture of itself; the rest of the
    // file is untouched.
    const canvas = await rasterise( alone, box.w, box.h );

    if ( ! canvas ) return null;

    rastered.push( name );

    return {
      name,
      kind: 'picture',
      box,
      hidden: node.getAttribute( 'display' ) === 'none',
      opacity: Number( node.getAttribute( 'opacity' ) ?? 1 ) || 1,
      canvas,
    };
  };

  if ( groups.length > 1 ) {
    for ( const [ index, group ] of groups.slice( 0, MOST_PIECES ).entries() ) {
      const name = group.getAttribute( 'data-name' ) || group.getAttribute( 'id' ) || `Layer ${ index + 1 }`;
      const box = boxes.get( group ) ?? { x: 0, y: 0, w: size.w, h: size.h };
      const piece = box.w > 0 && box.h > 0 ? await asPiece( group, name, box ) : null;

      if ( piece ) pieces.push( piece );
    }
  } else {
    const piece = await asPiece( root, file.name.replace( /\.svg$/i, '' ) || 'Artwork', { x: 0, y: 0, w: size.w, h: size.h } );

    if ( piece ) pieces.push( piece );
  }

  if ( ! pieces.length ) {
    throw new ImportProblem( 'There is nothing in that drawing to import.' );
  }

  if ( groups.length <= 1 ) {
    notes.push( 'This drawing has no named groups, so it came in as one piece. Export it from Illustrator with its layers to bring them in separately.' );
  }

  if ( rastered.length ) {
    notes.push(
      `Gradients, photographs and clipping paths cannot be redrawn as outlines, so these came in as pictures: ${ rastered.slice( 0, 6 ).join( ', ' ) }.`
    );
  }

  // Live text is worth saying something about, because a shop that wanted
  // editable words will otherwise wonder where they went.
  if ( root.getElementsByTagName( 'text' ).length ) {
    notes.push( 'The words in this drawing came in as artwork. To keep them editable, retype them with the Text tool.' );
  }

  void haveFont;

  return { source: 'svg', width: size.w, height: size.h, pieces, notes };
}

/**
 * A group lifted out into an SVG of its own, keeping the file's own frame.
 *
 * Only the definitions this group actually refers to come with it. Carrying
 * none of them meant a group whose fill is `url(#gradient)` arrived with the
 * gradient missing - accepted as outlines and drawn in the fallback colour.
 * Carrying all of them meant every group in the file inherited the one
 * gradient in it, and three plain groups became three pictures. What a group
 * points at travels with it; what it does not, does not.
 */
function standalone( root: Element, node: Element, box?: { x: number; y: number; w: number; h: number } ): string {
  if ( node === root ) return root.outerHTML;

  const svg = root.cloneNode( false ) as Element;
  const wanted = referenced( root, node );

  if ( wanted.length ) {
    const defs = root.ownerDocument.createElementNS( root.namespaceURI, 'defs' );

    for ( const element of wanted ) defs.appendChild( element.cloneNode( true ) );

    svg.appendChild( defs );
  }

  // A stylesheet governs everything, so it travels whatever the group is.
  for ( const style of [ ...root.getElementsByTagName( 'style' ) ] ) {
    svg.appendChild( style.cloneNode( true ) );
  }

  svg.appendChild( node.cloneNode( true ) );

  /*
   * Cropped to the group itself. The frame the file drew is irrelevant once a
   * group is on its own: what matters is that the piece's picture and the
   * piece's box are the same shape, or it arrives squashed.
   */
  if ( box && box.w > 0 && box.h > 0 ) {
    const user = toUserUnits( root, box );

    svg.setAttribute( 'viewBox', `${ user.x } ${ user.y } ${ user.w } ${ user.h }` );
    svg.setAttribute( 'width', `${ box.w }mm` );
    svg.setAttribute( 'height', `${ box.h }mm` );
  }

  return svg.outerHTML;
}

/**
 * Every group's box, in millimetres, as the browser lays the file out.
 *
 * The file is put into the page out of sight and measured, then taken away
 * again: getBBox needs a real layout, and nothing else accounts for transforms
 * and stroke widths the way the renderer that will draw it does.
 */
function measure( root: Element, groups: Element[] ): Map< Element, { x: number; y: number; w: number; h: number } > {
  const boxes = new Map< Element, { x: number; y: number; w: number; h: number } >();
  const holder = document.createElement( 'div' );

  holder.style.cssText = 'position:fixed;left:-10000px;top:0;width:0;height:0;overflow:hidden';

  const copy = root.cloneNode( true ) as SVGSVGElement;

  holder.appendChild( copy );
  document.body.appendChild( holder );

  try {
    const perUnit = unitsToMm( root );

    groups.forEach( ( group, index ) => {
      const drawn = copy.children[ [ ...root.children ].indexOf( group ) ] as SVGGraphicsElement | undefined;

      if ( ! drawn || typeof drawn.getBBox !== 'function' ) return;

      try {
        const box = drawn.getBBox();

        boxes.set( group, {
          x: mm( box.x * perUnit.x ),
          y: mm( box.y * perUnit.y ),
          w: mm( box.width * perUnit.x ),
          h: mm( box.height * perUnit.y ),
        } );
      } catch ( error ) {
        void index;
      }
    } );
  } finally {
    holder.remove();
  }

  return boxes;
}

/** How many millimetres one of the file's own units is, across and down. */
function unitsToMm( root: Element ): { x: number; y: number } {
  const size = svgSize( root );
  const box = ( root.getAttribute( 'viewBox' ) ?? '' ).split( /[\s,]+/ ).map( Number );

  if ( ! size || box.length !== 4 || ! ( box[ 2 ]! > 0 ) || ! ( box[ 3 ]! > 0 ) ) {
    return { x: 25.4 / 96, y: 25.4 / 96 };
  }

  return { x: size.w / box[ 2 ]!, y: size.h / box[ 3 ]! };
}

/** A box in millimetres, back in the file's own units. */
function toUserUnits( root: Element, box: { x: number; y: number; w: number; h: number } ): { x: number; y: number; w: number; h: number } {
  const per = unitsToMm( root );

  return {
    x: box.x / ( per.x || 1 ),
    y: box.y / ( per.y || 1 ),
    w: box.w / ( per.x || 1 ),
    h: box.h / ( per.y || 1 ),
  };
}

/**
 * The elements a group points at: gradients, patterns, clipping paths, masks,
 * filters and anything they point at in turn.
 */
function referenced( root: Element, node: Element ): Element[] {
  const byId = new Map< string, Element >();

  for ( const element of [ ...root.getElementsByTagName( '*' ) ] ) {
    const id = element.getAttribute( 'id' );

    if ( id ) byId.set( id, element );
  }

  const found = new Map< string, Element >();

  const collect = ( from: Element, depth: number ) => {
    if ( depth > 6 ) return;

    for ( const element of [ from, ...from.getElementsByTagName( '*' ) ] ) {
      for ( const attribute of [ ...element.attributes ] ) {
        const ids = [ ...attribute.value.matchAll( /url\(\s*#([^)\s]+)\s*\)/g ) ].map( ( m ) => m[ 1 ]! );

        if ( /^(xlink:)?href$/.test( attribute.name ) && attribute.value.startsWith( '#' ) ) {
          ids.push( attribute.value.slice( 1 ) );
        }

        for ( const id of ids ) {
          const target = byId.get( id );

          if ( ! target || found.has( id ) ) continue;

          found.set( id, target );
          collect( target, depth + 1 );
        }
      }
    }
  };

  collect( node, 0 );

  return [ ...found.values() ];
}

/** What the file says its size is, in millimetres. */
function svgSize( root: Element ): { w: number; h: number } | null {
  const length = ( value: string | null ): number => {
    if ( ! value ) return 0;

    const number = parseFloat( value );

    if ( ! Number.isFinite( number ) ) return 0;

    // The units an illustrator's export actually uses.
    if ( /mm$/.test( value ) ) return number;
    if ( /cm$/.test( value ) ) return number * 10;
    if ( /in$/.test( value ) ) return number * 25.4;
    if ( /pt$/.test( value ) ) return number * PT_TO_MM;
    if ( /pc$/.test( value ) ) return number * 12 * PT_TO_MM;

    // Bare numbers and px are user units: 96 to the inch, as the web has it.
    return ( number / 96 ) * 25.4;
  };

  const w = length( root.getAttribute( 'width' ) );
  const h = length( root.getAttribute( 'height' ) );

  if ( w > 0 && h > 0 ) return { w: mm( w ), h: mm( h ) };

  const box = ( root.getAttribute( 'viewBox' ) ?? '' ).split( /[\s,]+/ ).map( Number );

  if ( box.length === 4 && box[ 2 ]! > 0 && box[ 3 ]! > 0 ) {
    return { w: mm( ( box[ 2 ]! / 96 ) * 25.4 ), h: mm( ( box[ 3 ]! / 96 ) * 25.4 ) };
  }

  return null;
}

/** An SVG fragment drawn into a canvas at press resolution. */
function rasterise( svg: string, widthMm: number, heightMm: number ): Promise<HTMLCanvasElement | null> {
  return new Promise( ( resolve ) => {
    const canvas = document.createElement( 'canvas' );

    canvas.width = Math.max( 1, Math.min( 8000, Math.round( ( widthMm / 25.4 ) * PRINT_DPI ) ) );
    canvas.height = Math.max( 1, Math.min( 8000, Math.round( ( heightMm / 25.4 ) * PRINT_DPI ) ) );

    const image = new Image();
    // A data URL rather than a blob URL: a blob URL taints nothing but has to
    // be revoked, and this one is used once and thrown away with the image.
    const url = `data:image/svg+xml;charset=utf-8,${ encodeURIComponent( svg ) }`;

    image.onload = () => {
      canvas.getContext( '2d' )?.drawImage( image, 0, 0, canvas.width, canvas.height );
      resolve( canvas );
    };

    image.onerror = () => resolve( null );
    image.src = url;
  } );
}

/* ---------------------------------------------------------------
 * Illustrator
 * ------------------------------------------------------------ */

/**
 * An .ai file (or a PDF), as one piece of artwork at press resolution.
 *
 * Read through the same engine a browser reads PDFs with, so what arrives is
 * what Acrobat would show - including the colours, which is the part that
 * matters most on artwork that started life in CMYK.
 */
export async function readIllustrator( file: File ): Promise<ImportDoc> {
  const pdfjs = await import( 'pdfjs-dist' );

  // The worker is a separate file; Vite gives us its URL.
  pdfjs.GlobalWorkerOptions.workerSrc = ( await import( 'pdfjs-dist/build/pdf.worker.min.mjs?url' ) ).default;

  let doc;

  try {
    doc = await pdfjs.getDocument( { data: await file.arrayBuffer() } ).promise;
  } catch ( error ) {
    throw new ImportProblem( `That Illustrator file could not be read (${ error instanceof Error ? error.message : String( error ) }).` );
  }

  const page = await doc.getPage( 1 );
  const view = page.getViewport( { scale: 1 } );
  const widthMm = mm( view.width * PT_TO_MM );
  const heightMm = mm( view.height * PT_TO_MM );

  const scale = Math.min( PRINT_DPI / 72, 8000 / Math.max( view.width, view.height ) );
  const viewport = page.getViewport( { scale } );
  const canvas = document.createElement( 'canvas' );

  canvas.width = Math.round( viewport.width );
  canvas.height = Math.round( viewport.height );

  const context = canvas.getContext( '2d' );

  if ( ! context ) {
    throw new ImportProblem( 'This browser would not draw that file.' );
  }

  await page.render( { canvasContext: context, viewport, canvas } ).promise;

  const notes = [
    'Illustrator keeps its layers in its own private data, which no other program can read, so this came in as one piece of artwork.',
    'To bring the layers in separately, open it in Illustrator and use File ▸ Export ▸ SVG.',
  ];

  if ( doc.numPages > 1 ) {
    notes.push( `Only the first of its ${ doc.numPages } pages was imported.` );
  }

  return {
    source: 'ai',
    width: widthMm,
    height: heightMm,
    pieces: [
      {
        name: file.name.replace( /\.(ai|pdf)$/i, '' ) || 'Artwork',
        kind: 'picture',
        box: { x: 0, y: 0, w: widthMm, h: heightMm },
        hidden: false,
        opacity: 1,
        canvas,
      },
    ],
    notes,
  };
}

/** Which reader a file needs, by what it is called. */
export function readerFor( file: File ): 'psd' | 'svg' | 'ai' | null {
  const name = file.name.toLowerCase();

  if ( name.endsWith( '.psd' ) || name.endsWith( '.psb' ) ) return 'psd';
  if ( name.endsWith( '.svg' ) ) return 'svg';
  if ( name.endsWith( '.ai' ) || name.endsWith( '.pdf' ) ) return 'ai';

  return null;
}
